import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MelonCard from "@/components/feature/MelonCard";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { hotSearches, melons } from "@/mocks/gossip";

const SITE_URL = "https://pjdflq.cn";

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const melon = melons.find((item) => item.id === id);

  useDocumentMeta({
    title: melon ? `${melon.title} - 瓜田吃瓜` : "内容不存在 - 瓜田吃瓜",
    description: melon
      ? melon.summary
      : "这条内容可能已经调整或合并，回到瓜田广场看看其它正在跟进的内容。",
    keywords: melon ? `${melon.categoryName},${melon.tags.join(",")},瓜田吃瓜,蹲后续` : "瓜田吃瓜",
    path: `/article/${id ?? ""}`,
    ogType: "article",
    jsonLd: melon
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: melon.title,
            description: melon.summary,
            datePublished: melon.date,
            articleSection: melon.categoryName,
            keywords: melon.tags.join(","),
            inLanguage: "zh-CN",
            author: { "@type": "Person", name: melon.author },
            publisher: { "@type": "Organization", name: "瓜田吃瓜" },
            mainEntityOfPage: `${SITE_URL}/article/${melon.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "瓜田广场", item: `${SITE_URL}/plaza` },
              {
                "@type": "ListItem",
                position: 3,
                name: melon.title,
                item: `${SITE_URL}/article/${melon.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!melon) {
    return (
      <>
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-emotion-unhappy-line text-3xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            这条瓜好像被吃完了
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            页面可能已调整或合并，回到瓜田广场看看其它正在跟进的内容吧。
          </p>
          <Link
            to="/plaza"
            className="mt-6 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-600 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
          >
            返回瓜田广场
            <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
          </Link>
        </main>
        <SiteFooter />
      </>
    );
  }

  const sameCategory = melons.filter(
    (item) => item.id !== melon.id && item.categoryId === melon.categoryId,
  );
  const others = melons.filter(
    (item) => item.id !== melon.id && item.categoryId !== melon.categoryId,
  );
  const related = [...sameCategory, ...others].slice(0, 3);

  return (
    <>
      <SiteHeader />
      <main className="w-full">
        <div className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-5 md:px-8 md:py-6">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "瓜田广场", to: "/plaza" },
                { label: melon.title },
              ]}
            />
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-12">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <article className="lg:col-span-8">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Link
                  to={`/plaza?category=${melon.categoryId}`}
                  className="rounded-full bg-primary-100 px-3 py-1 font-medium text-primary-800 transition-colors hover:bg-primary-200"
                >
                  {melon.categoryName}
                </Link>
                {melon.heat && (
                  <span className="rounded-full bg-accent-500 px-3 py-1 font-medium text-background-50">
                    {melon.heat}
                  </span>
                )}
                <span className="text-foreground-400">更新于 {melon.date}</span>
              </div>

              <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
                {melon.title}
              </h1>

              <div className="mt-5 flex flex-wrap items-center gap-5 border-y border-background-200 py-4 text-sm text-foreground-600">
                <span className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-background-50">
                    {melon.author.slice(0, 1)}
                  </span>
                  {melon.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" aria-hidden="true"></i>
                  {melon.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  阅读约 {melon.readTime}
                </span>
              </div>

              <figure className="mt-7">
                <div className="h-60 w-full overflow-hidden rounded-lg bg-background-100 md:h-96">
                  <img
                    src={melon.cover}
                    alt={melon.coverAlt}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <figcaption className="mt-3 text-xs text-foreground-500">
                  {melon.coverAlt}（示意图）
                </figcaption>
              </figure>

              <blockquote className="mt-8 rounded-lg border-l-4 border-accent-500 bg-accent-50 p-5 md:p-6">
                <p className="font-heading text-base font-bold leading-relaxed text-accent-900 md:text-lg">
                  {melon.highlight}
                </p>
              </blockquote>

              <div className="mt-8 space-y-5">
                {melon.content.map((paragraph, index) => (
                  <p key={`${melon.id}-p-${index}`} className="text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500">相关标签：</span>
                {melon.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-8 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-5 sm:flex-row sm:items-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-500 text-lg font-bold text-background-50">
                  {melon.author.slice(0, 1)}
                </span>
                <div className="flex-1">
                  <p className="font-heading text-base font-bold text-foreground-950">
                    {melon.author}
                  </p>
                  <p className="mt-1 text-sm text-foreground-600">
                    负责把公开信息和时间线整理清楚，尽量少一点情绪，多一点可核实的细节。
                  </p>
                </div>
                <Link
                  to="/plaza"
                  className="inline-flex whitespace-nowrap items-center gap-1 rounded-full border border-primary-300 px-4 py-2 text-sm font-medium text-primary-800 transition-colors hover:bg-primary-100"
                >
                  看 TA 的更多内容
                </Link>
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="flex flex-col gap-6 lg:sticky lg:top-24">
                <div className="rounded-lg border border-primary-200 bg-primary-100/60 p-5">
                  <h2 className="flex items-center gap-2 font-heading text-base font-bold text-primary-900">
                    <i className="ri-notification-3-line text-lg" aria-hidden="true"></i>
                    蹲这条瓜的后续
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                    这条时间线还在更新。留下邮箱订阅，有新的进展我们会第一时间通知你。
                  </p>
                  <Link
                    to="/#follow"
                    className="mt-4 inline-flex w-full items-center justify-center gap-1 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
                  >
                    订阅蹲后续
                  </Link>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                    <i className="ri-fire-line text-lg text-accent-600" aria-hidden="true"></i>
                    热搜快照
                  </h2>
                  <ol className="mt-4 space-y-3">
                    {hotSearches.slice(0, 5).map((item) => (
                      <li key={item.id}>
                        <Link to="/plaza" className="group flex items-start gap-3">
                          <span
                            className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                              item.rank <= 3
                                ? "bg-accent-500 text-background-50"
                                : "bg-background-300/70 text-foreground-700"
                            }`}
                          >
                            {item.rank}
                          </span>
                          <span className="min-w-0 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-700">
                            {item.title}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </aside>
          </div>

          <section className="mt-14" aria-labelledby="related-title">
            <div className="mb-6 flex items-center justify-between">
              <h2 id="related-title" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                相关推荐
              </h2>
              <Link
                to="/plaza"
                className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 hover:text-primary-800"
              >
                查看全部
                <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <MelonCard key={item.id} melon={item} />
              ))}
            </div>
          </section>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}